import { apiClient } from './client';

// Customer Dashboard Types
export interface PortfolioStats {
  total_investment: number;
  total_profit: number;
  total_value: number;
  monthly_return: number;
  active_investments: number;
  completed_investments: number;
  average_rate: number;
  roi_percentage: number;
}

export interface CustomerInvestment {
  id: number;
  bank_name: string;
  bank_logo_url?: string;
  amount: number;
  interest_rate: number;
  start_date?: string;
  end_date?: string;
  maturity_date?: string;
  status: 'active' | 'inactive' | 'cancelled' | 'pending' | 'deposited';
  duration_months?: number;
  expected_profit: number;
  current_value: number;
  days_remaining?: number;
}

export interface MonthlyData {
  month: string;
  investment: number;
  profit: number;
  total: number;
}

// Updated to match backend TransactionType enum
export interface CustomerTransaction {
  id: number;
  type: 'deposit' | 'withdrawal' | 'interest_payment' | 'investment_creation' | 'investment_maturity' | 'penalty' | 'fee' | 'refund' | 'investment' | 'bonus';
  amount: number;
  description: string;
  date?: string;
  status: 'completed' | 'pending' | 'failed' | 'cancelled' | 'processing';
  bank_name?: string;
  investment_id?: number;
  user_name?: string;
  reference_number?: string;
  created_at?: string;
  updated_at?: string;
  processed_at?: string;
}

// Transaction summary from backend
export interface TransactionSummary {
  user_id: number;
  date_range_start: string;
  date_range_end: string;
  total_deposits: number;
  total_withdrawals: number;
  total_interest_payments: number;
  total_fees: number;
  net_amount: number;
  transaction_count: number;
}

export interface CustomerProfile {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  date_of_birth?: string;
  national_id?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  two_factor_enabled: boolean;
  admin_created?: boolean;
  created_at: string;
  updated_at?: string;
  // joint account
  is_joint_account?: boolean;
  partner_first_name?: string;
  partner_last_name?: string;
  partner_birth_date?: string;
  partner_national_id?: string;
  partner_address?: string;
  partner_city?: string;
  partner_postal_code?: string;
  partner_country?: string;
}

export interface CustomerProfileUpdate {
  first_name?: string;
  last_name?: string;
  phone?: string;
  date_of_birth?: string;
  national_id?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
}

export interface DashboardData {
  portfolio_stats: PortfolioStats;
  recent_investments: CustomerInvestment[];
  recent_transactions: CustomerTransaction[];
  monthly_data: MonthlyData[];
}

export interface InvestmentListParams {
  page?: number;
  page_size?: number;
  status?: 'active' | 'inactive' | 'cancelled' | 'pending' | 'deposited';
  bank_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface InvestmentListResponse {
  investments: CustomerInvestment[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

// Updated to match backend TransactionType enum
export interface TransactionListParams {
  page?: number;
  page_size?: number;
  transaction_type?: 'deposit' | 'bonus' | 'withdrawal' | 'interest_payment' | 'investment_creation' | 'investment_maturity' | 'penalty' | 'fee' | 'refund' | 'investment';
  status?: 'completed' | 'pending' | 'failed' | 'cancelled' | 'processing';
  start_date?: string;
  end_date?: string;
  amount_min?: number;
  amount_max?: number;
  date_from?: string;
  date_to?: string;
}

export interface TransactionListResponse {
  transactions: CustomerTransaction[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

// Customer API Service
export const customerApi = {
  // Dashboard Data
  dashboard: {
    // Get complete dashboard data
    async getData(): Promise<DashboardData> {
      try {
        const response = await apiClient.get('/api/v1/customers/dashboard');
        return response.data.data; // Extract the data property from backend response
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw new Error('Dashboard verileri getirilemedi');
      }
    },

    // Get portfolio statistics
    async getPortfolioStats(): Promise<PortfolioStats> {
      try {
        const response = await apiClient.get('/api/v1/customers/portfolio/stats');
        return response.data;
      } catch (error) {
        console.error('Error fetching portfolio stats:', error);
        throw new Error('Portföy istatistikleri getirilemedi');
      }
    },

    // Get monthly chart data
    async getMonthlyData(months: number = 12): Promise<MonthlyData[]> {
      try {
        const response = await apiClient.get('/api/v1/customers/portfolio/monthly', {
          params: { months }
        });
        return response.data.monthly_data || [];
      } catch (error) {
        console.error('Error fetching monthly data:', error);
        throw new Error('Aylık veriler getirilemedi');
      }
    }
  },

  // Customer Profile
  profile: {
    // Get current customer profile
    async get(): Promise<CustomerProfile> {
      try {
        const response = await apiClient.get('/api/v1/customers/profile');
        const body = response.data;
        const raw: any = body?.data ?? body?.profile ?? body;

        // If response contains nested user/security/preferences, normalize it
        if (raw && typeof raw === 'object' && 'user' in raw) {
          const user = raw.user || {};
          const prefs = raw.preferences || {};
          const notifs = prefs.notifications || {};
          const security = raw.security || {};
          const profile: CustomerProfile = {
            id: user.id,
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            phone: user.phone || '',
            date_of_birth: user.date_of_birth || undefined,
            national_id: user.national_id || undefined,
            address: user.address || undefined,
            city: user.city || undefined,
            country: user.country || undefined,
            postal_code: user.postal_code || undefined,
            email_notifications: Boolean(notifs.email_alerts),
            sms_notifications: Boolean(notifs.sms_alerts),
            two_factor_enabled: Boolean(security.two_factor_enabled),
            admin_created: Boolean(user.admin_created),
            created_at: user.created_at || new Date().toISOString(),
            updated_at: user.updated_at || undefined,
          };
          return profile;
        }

        // Otherwise assume it's already in the expected shape
        return raw as CustomerProfile;
      } catch (error) {
        console.error('Error fetching customer profile:', error);
        throw new Error('Profil bilgileri getirilemedi');
      }
    },

    // Update customer profile
    async update(profileData: CustomerProfileUpdate): Promise<CustomerProfile> {
      try {
        const response = await apiClient.put('/api/v1/customers/profile', profileData);
        return response.data;
      } catch (error: any) {
        console.error('Error updating profile:', error);
        const message = error.response?.data?.detail || 'Profil güncellenemedi';
        throw new Error(message);
      }
    },

    // Change password
    async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
      try {
        const response = await apiClient.post('/api/v1/customers/profile/change-password', {
          current_password: currentPassword,
          new_password: newPassword
        });
        return response.data;
      } catch (error: any) {
        console.error('Error changing password:', error);
        const message = error.response?.data?.detail || 'Şifre değiştirilemedi';
        throw new Error(message);
      }
    }
  },

  // Customer Investments
  investments: {
    // List customer investments
    async list(params: InvestmentListParams = {}): Promise<InvestmentListResponse> {
      try {
        const response = await apiClient.get('/api/v1/customers/investments', {
          params: params
        });
        return response.data.data; // Extract data from backend response
      } catch (error) {
        console.error('Error fetching investments:', error);
        throw new Error('Yatırımlar getirilemedi');
      }
    },

    // Get investment details
    async getById(investmentId: number): Promise<CustomerInvestment> {
      try {
        const response = await apiClient.get(`/api/v1/customers/investments/${investmentId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching investment details:', error);
        throw new Error('Yatırım detayları getirilemedi');
      }
    },

    // Create new investment
    async create(investmentData: any): Promise<CustomerInvestment> {
      try {
        const response = await apiClient.post('/api/v1/customers/investments', investmentData);
        return response.data;
      } catch (error: any) {
        console.error('Error creating investment:', error);
        const message = error.response?.data?.detail || 'Yatırım oluşturulamadı';
        throw new Error(message);
      }
    },

    // Calculate investment
    async calculate(bankId: number, amount: number, duration: number): Promise<any> {
      try {
        const response = await apiClient.post('/api/v1/customers/investments/calculate', {
          bank_id: bankId,
          amount,
          duration_months: duration
        });
        return response.data;
      } catch (error) {
        console.error('Error calculating investment:', error);
        throw new Error('Yatırım hesaplaması yapılamadı');
      }
    }
  },

  // Customer Transactions
  transactions: {
    // List customer transactions with enhanced filtering
    async list(params: TransactionListParams = {}): Promise<TransactionListResponse> {
      try {
        const response = await apiClient.get('/api/v1/customers/transactions', { 
          params: params
        });
        return response.data.data; // Extract data from backend response
      } catch (error) {
        console.error('Error fetching transactions:', error);
        throw new Error('İşlemler getirilemedi');
      }
    },

    // Get transaction details
    async getById(transactionId: number): Promise<CustomerTransaction> {
      try {
        const response = await apiClient.get(`/api/v1/customers/transactions/${transactionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching transaction details:', error);
        throw new Error('İşlem detayları getirilemedi');
      }
    },

    // Get transaction summary for current user
    async getSummary(dateFrom?: string, dateTo?: string): Promise<TransactionSummary> {
      try {
        const params: any = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        
        const response = await apiClient.get('/api/v1/customers/transactions/summary', { params });
        return response.data;
      } catch (error) {
        console.error('Error fetching transaction summary:', error);
        throw new Error('İşlem özeti getirilemedi');
      }
    }
  }
}; 